// Expense approvals: the finance approval queue for employee expense
// claims. Expense capture lives in HR — this page never takes a new claim,
// it only approves or rejects what is already waiting and posts approved
// reimbursements to the ledger. The list endpoint returns full items, so
// review opens a focused drawer over the list with no second fetch.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { useEffect, useState } from "react";
import { DeepLinkCard } from "@/components/DeepLinkCard";
import { StatusFilter } from "@/components/StatusFilter";
import { CardField } from "@/components/RecordCard";
import {
  approveExpenseReport,
  type ExpenseReport,
  listExpenseReports,
  listReimbursementPostings,
  postExpenseReimbursements,
  type ReimbursementPosting,
  rejectExpenseReport,
  reimburseExpenseReport,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

type DecisionKind = "approve" | "reject";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "approved":
    case "reimbursed":
      return "success";
    case "submitted":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  switch (status.toLowerCase()) {
    case "submitted":
      return "Waiting approval";
    default: {
      const lower = status.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }
  }
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** User ids have no label lookup — "Claimant" is the readable primary label
 * and the raw id is shown as un-truncated secondary text. */
function ClaimantLabel({ id }: { id: string }) {
  return (
    <span className="block min-w-0">
      <span className="block font-medium">Claimant</span>
      <span
        className="block break-all font-mono text-xs text-[var(--muted-foreground)]"
        title={id}
      >
        {id}
      </span>
    </span>
  );
}

export function ExpenseApprovalsPage() {
  const { scope } = useOrganization();
  const [reports, setReports] = useState<ExpenseReport[] | null>(null);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [postings, setPostings] = useState<ReimbursementPosting[] | null>(null);
  const [postingsError, setPostingsError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<string>("submitted");
  const [query, setQuery] = useState("");
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [note, setNote] = useState<{ reportId: string | null; value: string }>({
    reportId: null,
    value: "",
  });
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [pendingDecision, setPendingDecision] = useState<{
    kind: DecisionKind;
    report: ExpenseReport;
  } | null>(null);
  const [acting, setActing] = useState(false);
  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadReports = () => {
    setReportsError(null);
    listExpenseReports()
      .then(setReports)
      .catch((cause: unknown) => setReportsError(toErrorMessage(cause)));
  };

  const loadPostings = () => {
    setPostingsError(null);
    listReimbursementPostings()
      .then(setPostings)
      .catch((cause: unknown) => setPostingsError(toErrorMessage(cause)));
  };

  const refreshAll = () => {
    loadReports();
    loadPostings();
  };

  useEffect(refreshAll, []);

  const review = (reports ?? []).find((report) => report.id === reviewId) ?? null;
  const activeNote = review && note.reportId === review.id ? note.value : "";
  const pendingNote =
    pendingDecision && note.reportId === pendingDecision.report.id
      ? note.value.trim()
      : "";

  const statusCounts = (reports ?? []).reduce<Record<string, number>>(
    (counts, report) => {
      const key = report.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );

  const normalizedQuery = query.trim().toLowerCase();
  const visible = (reports ?? []).filter((report) => {
    if (statusTab && report.status.toLowerCase() !== statusTab) return false;
    if (!normalizedQuery) return true;
    return (
      report.title.toLowerCase().includes(normalizedQuery) ||
      (report.description ?? "").toLowerCase().includes(normalizedQuery) ||
      report.submitted_by.toLowerCase().includes(normalizedQuery)
    );
  });

  const postingLines = postings ?? [];
  const postingClaimCount = new Set(
    postingLines.map((posting) => posting.report_id),
  ).size;
  const waitingTotal = postingLines.reduce(
    (sum, posting) => sum + posting.amount_cents,
    0,
  );

  const openReview = (report: ExpenseReport) => {
    setReviewId(report.id);
    setNote({ reportId: report.id, value: "" });
    setReviewError(null);
  };

  const closeReview = () => {
    setReviewId(null);
    setReviewError(null);
    setPendingDecision(null);
  };

  const askDecision = (kind: DecisionKind) => {
    if (!review) return;
    const value = note.reportId === review.id ? note.value : "";
    if (kind === "reject" && !value.trim()) {
      setReviewError("Add a note saying why — it goes back to the claimant.");
      return;
    }
    setReviewError(null);
    setPendingDecision({ kind, report: review });
  };

  const runDecision = async () => {
    if (!pendingDecision) return;
    const { kind, report } = pendingDecision;
    const value = note.reportId === report.id ? note.value.trim() : "";
    setActing(true);
    setReviewError(null);
    setNotice(null);
    try {
      if (kind === "approve") {
        await approveExpenseReport(report.id, value ? { note: value } : {});
        setNotice(
          `Approved “${report.title}” — its lines can now be posted as a reimbursement below.`,
        );
      } else {
        await rejectExpenseReport(report.id, { note: value });
        setNotice(`Rejected “${report.title}” — it goes back to the claimant.`);
      }
      setPendingDecision(null);
      closeReview();
      refreshAll();
    } catch (cause) {
      setPendingDecision(null);
      setReviewError(
        `${kind === "approve" ? "Approve" : "Reject"} failed: ${toErrorMessage(cause)}`,
      );
    } finally {
      setActing(false);
    }
  };

  const settleReport = async (report: ExpenseReport) => {
    const legalEntityId = report.legal_entity_id ?? scope?.legal_entity_id;
    if (!legalEntityId) {
      setReportsError("Select a legal entity before settling a reimbursement.");
      return;
    }
    setSettlingId(report.id);
    setReportsError(null);
    setNotice(null);
    try {
      await reimburseExpenseReport(report.id, {
        legal_entity_id: legalEntityId,
        paid_at: new Date().toISOString().slice(0, 10),
      });
      setNotice(
        `Settled “${report.title}” — the employee payable is cleared. Bookkeeping only; no money was sent.`,
      );
      refreshAll();
    } catch (cause) {
      setReportsError(`Settle failed: ${toErrorMessage(cause)}`);
    } finally {
      setSettlingId(null);
    }
  };

  const postNow = async () => {
    setPostConfirmOpen(false);
    setPosting(true);
    setPostError(null);
    setNotice(null);
    try {
      const result = await postExpenseReimbursements();
      setNotice(
        result.posted_count === 0
          ? "Nothing approved and waiting — no reimbursements posted."
          : `Posted ${result.posted_count} ${result.posted_count === 1 ? "reimbursement" : "reimbursements"} to the ledger.`,
      );
      refreshAll();
    } catch (cause) {
      setPostError(`Post reimbursements failed: ${toErrorMessage(cause)}`);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Expenses"
        description="Employee expense claims waiting on finance — approve or send back. New claims are submitted in HR, not here."
      />

      <DeepLinkCard
        target="hrPayroll"
        scope={{
          organization_id: scope?.organization_id,
        }}
        title="Submit an expense claim"
        body="Expense capture lives in HR and payroll — claims arrive in this queue once submitted there."
      />

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      <StatusFilter
        label="Filter claims"
        value={statusTab}
        onChange={setStatusTab}
        options={[
          { value: "", label: "All", count: (reports ?? []).length },
          {
            value: "submitted",
            label: "Waiting approval",
            count: statusCounts.submitted ?? 0,
          },
          { value: "approved", label: "Approved", count: statusCounts.approved ?? 0 },
          { value: "rejected", label: "Rejected", count: statusCounts.rejected ?? 0 },
          {
            value: "reimbursed",
            label: "Reimbursed",
            count: statusCounts.reimbursed ?? 0,
          },
          { value: "draft", label: "Draft", count: statusCounts.draft ?? 0 },
        ]}
      />
        <div className="grid gap-3">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              refreshAll();
            }}
            className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
          >
            <label
              htmlFor="expense-search"
              className="grid min-w-52 flex-1 gap-1 text-xs font-medium"
            >
              Search claims
              <Input
                id="expense-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Claim title or claimant id"
              />
            </label>
            <Button type="submit" variant="secondary">
              Refresh
            </Button>
          </form>

          {reportsError ? (
            <div
              role="alert"
              className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
            >
              <p>Couldn’t load expense claims: {reportsError}</p>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={loadReports}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : reports === null ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Loading expense claims…
            </p>
          ) : reports.length === 0 ? (
            <EmptyState
              title="No expense claims yet"
              reason="When the team submits expense claims in HR, the ones waiting on finance appear here for approval."
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="No claims match"
              reason={
                normalizedQuery
                  ? "No claims match this search in this status — try another tab or clear the search."
                  : "No claims currently carry this status — try another tab."
              }
              action={
                normalizedQuery ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setQuery("")}
                  >
                    Clear search
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
                <div className="table-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Claim</TableHead>
                        <TableHead>Claimed by</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visible.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>
                            <button
                              type="button"
                              onClick={() => openReview(report)}
                              className="text-left font-medium hover:underline"
                              title="Review this claim"
                            >
                              {report.title}
                            </button>
                            <span className="block text-xs text-[var(--muted-foreground)]">
                              {report.items.length}{" "}
                              {report.items.length === 1 ? "line" : "lines"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <ClaimantLabel id={report.submitted_by} />
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={statusTone(report.status)}
                              label={plainStatus(report.status)}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: report.total_minor,
                              currency: "USD",
                            })}
                          </TableCell>
                          <TableCell>
                            {report.status.toLowerCase() === "submitted" ? (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => openReview(report)}
                                disabled={acting}
                              >
                                Review
                              </Button>
                            ) : report.status.toLowerCase() === "approved" ? (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => settleReport(report)}
                                disabled={settlingId === report.id}
                              >
                                {settlingId === report.id
                                  ? "Settling…"
                                  : "Settle"}
                              </Button>
                            ) : (
                              <span className="text-xs text-[var(--muted-foreground)]">
                                —
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div
                className="stack-list grid gap-3 min-[64rem]:hidden"
                data-testid="expense-approvals-stack"
              >
                {visible.map((report) => (
                  <article
                    key={report.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => openReview(report)}
                        className="text-left font-medium hover:underline"
                        title="Review this claim"
                      >
                        {report.title}
                      </button>
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: report.total_minor,
                          currency: "USD",
                        })}
                      </span>
                    </div>
                    <CardField label="Claimed by">
                      <ClaimantLabel id={report.submitted_by} />
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={statusTone(report.status)}
                        label={plainStatus(report.status)}
                      />
                    </CardField>
                    <CardField label="Lines">
                      {report.items.length}{" "}
                      {report.items.length === 1 ? "line" : "lines"}
                    </CardField>
                    {report.status.toLowerCase() === "submitted" ? (
                      <div className="flex flex-wrap gap-2 pt-3">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => openReview(report)}
                          disabled={acting}
                        >
                          Review
                        </Button>
                      </div>
                    ) : report.status.toLowerCase() === "approved" ? (
                      <div className="flex flex-wrap gap-2 pt-3">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => settleReport(report)}
                          disabled={settlingId === report.id}
                        >
                          {settlingId === report.id ? "Settling…" : "Settle"}
                        </Button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </>
          )}
        </div>

      <section aria-label="Reimbursements" className="grid gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Pay back approved claims
            </h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              Approved lines waiting to post — posting writes them to the books
              as money owed back to the team.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setPostConfirmOpen(true)}
            disabled={posting || postings === null || postings.length === 0}
            title="Post every approved, unposted line to the ledger"
          >
            {posting ? "Posting…" : "Post reimbursements"}
          </Button>
        </div>

        {postingsError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            <p>
              Couldn’t load approved reimbursement lines: {postingsError}
            </p>
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={loadPostings}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : postings === null ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            Loading approved reimbursement lines…
          </p>
        ) : postings.length === 0 ? (
          <EmptyState
            title="Nothing waiting to post"
            reason="Approved claim lines appear here until they are posted to the ledger."
          />
        ) : (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Claim</TableHead>
                    <TableHead>Approved</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {postings.map((posting) => (
                    <TableRow key={posting.line_id}>
                      <TableCell>
                        {posting.title}
                        {posting.expense_account_code ? (
                          <span className="block font-mono text-xs text-[var(--muted-foreground)]">
                            {posting.expense_account_code}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {new Date(posting.approved_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: posting.amount_cents,
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} className="font-medium">
                      Waiting total
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatMoney({ cents: waitingTotal, currency: "USD" })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {postError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {postError}
          </div>
        ) : null}
      </section>

      {review ? (
        <Drawer
          open
          onOpenChange={(open) => {
            if (!open) closeReview();
          }}
          title={review.title}
          description="Review the claim facts, then approve it or send it back to the claimant."
          className="w-[min(34rem,100%)]"
          footer={
            review.status.toLowerCase() === "submitted" ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => askDecision("approve")}
                  disabled={acting}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => askDecision("reject")}
                  disabled={acting}
                >
                  Send back
                </Button>
              </div>
            ) : (
              <p className="text-sm text-[var(--muted-foreground)]">
                This claim is already {plainStatus(review.status).toLowerCase()}.
              </p>
            )
          }
        >
          <div className="grid gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <StatusDot
                tone={statusTone(review.status)}
                label={plainStatus(review.status)}
              />
              <span className="font-semibold tabular-nums">
                {formatMoney({
                  cents: review.total_minor,
                  currency: "USD",
                })}
              </span>
            </div>
            <CardField label="Claimed by">
              <ClaimantLabel id={review.submitted_by} />
            </CardField>
            <CardField label="Lines">
              {review.items.length}{" "}
              {review.items.length === 1 ? "line" : "lines"}
            </CardField>
            {review.description ? (
              <CardField label="Description">{review.description}</CardField>
            ) : null}
            {review.items.length === 0 ? (
              <p className="text-xs text-[var(--muted-foreground)]">
                This claim has no lines.
              </p>
            ) : (
              <ul className="grid gap-2">
                {review.items.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-baseline justify-between gap-3 rounded-lg border border-[var(--border)] p-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {item.category ?? "Expense"}
                      </span>
                      <span className="block text-xs text-[var(--muted-foreground)]">
                        {new Date(item.occurred_at).toLocaleDateString()}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatMoney({
                        cents: item.amount_minor,
                        currency: "USD",
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {review.status.toLowerCase() === "submitted" ? (
              <label
                htmlFor={`expense-note-${review.id}`}
                className="grid gap-1 text-xs font-medium"
              >
                Note (required to send back, optional to approve)
                <Input
                  id={`expense-note-${review.id}`}
                  value={activeNote}
                  onChange={(event) =>
                    setNote({ reportId: review.id, value: event.target.value })
                  }
                  placeholder="e.g. Approved — client travel"
                  disabled={acting}
                />
              </label>
            ) : null}
            {reviewError ? (
              <p role="alert" className="text-xs text-[var(--danger)]">
                {reviewError}
              </p>
            ) : null}
          </div>
        </Drawer>
      ) : null}

      <Dialog
        open={pendingDecision !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDecision(null);
        }}
        title={
          pendingDecision?.kind === "reject"
            ? "Send this claim back?"
            : "Approve this expense claim?"
        }
        description={
          pendingDecision?.kind === "reject"
            ? "Rejection returns the claim to the claimant with your note. It does not touch the ledger."
            : "Approving is a financial approval — the claim’s lines become eligible for reimbursement posting below. It does not move money."
        }
      >
        {pendingDecision ? (
          <div className="grid gap-4">
            <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)] p-3 text-sm">
              <p className="font-medium">{pendingDecision.report.title}</p>
              <p className="text-xs text-[var(--muted-foreground)]">
                Claimant {pendingDecision.report.submitted_by}
              </p>
              <p className="mt-1 font-semibold tabular-nums">
                {formatMoney({
                  cents: pendingDecision.report.total_minor,
                  currency: "USD",
                })}
              </p>
              {pendingNote ? (
                <p className="mt-2 text-xs">Note: “{pendingNote}”</p>
              ) : null}
            </div>
            <div className="actions-row">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPendingDecision(null)}
                disabled={acting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void runDecision()}
                disabled={acting}
              >
                {acting
                  ? "Working…"
                  : pendingDecision.kind === "approve"
                    ? "Approve claim"
                    : "Send back claim"}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={postConfirmOpen}
        onOpenChange={setPostConfirmOpen}
        title="Post expense reimbursements?"
        description="This posts reimbursement journals; it does not send money."
      >
        <div className="grid gap-4">
          <p className="text-sm">
            You are about to post{" "}
            <span className="font-semibold">
              {postingLines.length} approved{" "}
              {postingLines.length === 1 ? "line" : "lines"}
            </span>{" "}
            across{" "}
            <span className="font-semibold">
              {postingClaimCount}{" "}
              {postingClaimCount === 1 ? "claim" : "claims"}
            </span>{" "}
            for{" "}
            <span className="font-semibold">
              {formatMoney({ cents: waitingTotal, currency: "USD" })}
            </span>
            .
          </p>
          <p className="text-xs text-[var(--muted-foreground)]">
            Posting writes one balanced journal entry per line to the books. It
            cannot be undone from this screen.
          </p>
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPostConfirmOpen(false)}
              disabled={posting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void postNow()}
              disabled={posting}
            >
              {posting ? "Posting…" : "Post reimbursements"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
